/**
 * Complete the look
 *
 * Lets the shopper tick add-on products (chains, etc.) alongside the product they are
 * viewing. Shopify's product form can only ever post one line item, so when at least
 * one add-on is ticked this element takes the submit over and sends the main product
 * plus every ticked add-on as a single /cart/add.js request.
 */
if (!customElements.get('complete-the-look')) {
  customElements.define(
    'complete-the-look',
    class CompleteTheLook extends HTMLElement {
      connectedCallback() {
        if (this.initialised) return;
        this.initialised = true;

        this.sectionId = this.dataset.sectionId;
        this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
        this.basePrice = parseInt(this.dataset.basePrice, 10) || 0;

        this.totalElement = this.querySelector('[data-complete-look-total]');
        this.options = Array.from(this.querySelectorAll('[data-complete-look-option]'));

        this.onChange = this.onChange.bind(this);
        this.onFormSubmit = this.onFormSubmit.bind(this);
        this.onQuantityChange = this.onQuantityChange.bind(this);

        this.addEventListener('change', this.onChange);
        // Capture phase on the document so this runs before <product-form> sees the
        // submit and can stop it reaching the single-item handler.
        document.addEventListener('submit', this.onFormSubmit, true);
        document.addEventListener('change', this.onQuantityChange);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (!event || !event.data || event.data.sectionId !== this.sectionId) return;
            if (!event.data.variant) return;
            this.basePrice = event.data.variant.price;
            this.updateTotal();
          });
        }

        this.updateTotal();
      }

      disconnectedCallback() {
        document.removeEventListener('submit', this.onFormSubmit, true);
        document.removeEventListener('change', this.onQuantityChange);
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      }

      /**
       * Resolved on demand: the buy buttons block may be rendered after this one, and
       * the product form is replaced wholesale on some variant changes.
       */
      get form() {
        return document.getElementById(this.dataset.formId);
      }

      get quantity() {
        const input = document.getElementById(`Quantity-${this.sectionId}`);
        const value = parseInt(input ? input.value : 1, 10);
        return Number.isNaN(value) || value < 1 ? 1 : value;
      }

      get selectedOptions() {
        return this.options.filter((option) => {
          const checkbox = option.querySelector('[data-complete-look-checkbox]');
          return checkbox && checkbox.checked;
        });
      }

      onChange(event) {
        const variantSelect = event.target.closest('[data-complete-look-variant]');

        if (variantSelect) {
          const option = variantSelect.closest('[data-complete-look-option]');
          const selected = variantSelect.options[variantSelect.selectedIndex];
          const price = parseInt(selected.dataset.price, 10) || 0;

          option.dataset.variantId = selected.value;
          option.dataset.price = price;

          const priceElement = option.querySelector('[data-complete-look-price]');
          if (priceElement) priceElement.innerHTML = this.formatMoney(price);
        }

        this.updateTotal();
      }

      onQuantityChange(event) {
        if (event.target.name !== 'quantity') return;
        if (event.target.form !== this.form) return;
        this.updateTotal();
      }

      updateTotal() {
        let total = this.basePrice * this.quantity;

        this.options.forEach((option) => {
          const checkbox = option.querySelector('[data-complete-look-checkbox]');
          const checked = !!(checkbox && checkbox.checked);
          option.classList.toggle('is-selected', checked);
          if (checked) total += parseInt(option.dataset.price, 10) || 0;
        });

        if (this.totalElement) this.totalElement.innerHTML = this.formatMoney(total);
      }

      onFormSubmit(event) {
        const form = this.form;
        if (!form || event.target !== form) return;
        if (!this.selectedOptions.length) return;

        // A sold out or unselectable main variant is left to the theme's own handling.
        const variantInput = form.querySelector('.product-variant-id');
        if (!variantInput || variantInput.disabled) return;

        event.preventDefault();
        event.stopPropagation();
        this.addToCart();
      }

      /**
       * The main product line item, rebuilt from the product form so line item
       * properties (engraving, metal colour, ...) survive the takeover.
       */
      mainItem() {
        const formData = new FormData(this.form);
        const item = {
          id: formData.get('id'),
          quantity: parseInt(formData.get('quantity'), 10) || 1,
        };

        const properties = {};
        formData.forEach((value, key) => {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match && String(value).trim() !== '') properties[match[1]] = value;
        });
        if (Object.keys(properties).length) item.properties = properties;

        const sellingPlan = formData.get('selling_plan');
        if (sellingPlan) item.selling_plan = sellingPlan;

        return item;
      }

      addToCart() {
        const form = this.form;
        const submitButton = form.querySelector('[type="submit"]');
        const spinner = submitButton && submitButton.querySelector('.loading__spinner');
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        const items = [this.mainItem()].concat(
          this.selectedOptions.map((option) => ({
            id: option.dataset.variantId,
            quantity: 1,
          }))
        );

        const body = { items };
        if (cart) {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          cart.setActiveElement(document.activeElement);
        }

        this.setLoading(true, submitButton, spinner);
        this.showError();

        fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.showError(response.description || response.message);
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'complete-the-look',
                  errors: response.errors || response.description,
                  message: response.message,
                });
              }
              return;
            }

            this.clearSelection();

            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'complete-the-look',
                productVariantId: items[0].id,
                cartData: response,
              });
            }

            if (!cart) {
              window.location = window.routes.cart_url;
              return;
            }

            cart.renderContents(response);
          })
          .catch((error) => {
            console.error(error);
            this.showError(window.cartStrings ? window.cartStrings.error : '');
          })
          .finally(() => {
            this.setLoading(false, submitButton, spinner);
          });
      }

      clearSelection() {
        this.querySelectorAll('[data-complete-look-checkbox]').forEach((checkbox) => {
          checkbox.checked = false;
        });
        this.updateTotal();
      }

      setLoading(isLoading, submitButton, spinner) {
        this.classList.toggle('is-loading', isLoading);
        if (!submitButton) return;

        submitButton.classList.toggle('loading', isLoading);
        submitButton.setAttribute('aria-disabled', isLoading);
        if (spinner) spinner.classList.toggle('hidden', !isLoading);
      }

      showError(message) {
        const wrapper = this.form && this.form.closest('product-form');
        const errorWrapper = wrapper && wrapper.querySelector('.product-form__error-message-wrapper');
        if (!errorWrapper) return;

        errorWrapper.toggleAttribute('hidden', !message);
        const errorMessage = errorWrapper.querySelector('.product-form__error-message');
        if (errorMessage) errorMessage.textContent = message || '';
      }

      /** Mirrors Shopify's own formatMoney so totals match the Liquid-rendered prices. */
      formatMoney(cents) {
        const placeholder = /\{\{\s*(\w+)\s*\}\}/;
        const match = this.moneyFormat.match(placeholder);
        if (!match) return this.moneyFormat;

        const withDelimiters = (precision, thousands, decimal) => {
          const number = (cents / 100.0).toFixed(precision);
          const parts = number.split('.');
          const units = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
          return units + (parts[1] ? decimal + parts[1] : '');
        };

        let value;
        switch (match[1]) {
          case 'amount_no_decimals':
            value = withDelimiters(0, ',', '.');
            break;
          case 'amount_with_comma_separator':
            value = withDelimiters(2, '.', ',');
            break;
          case 'amount_no_decimals_with_comma_separator':
            value = withDelimiters(0, '.', ',');
            break;
          case 'amount_with_apostrophe_separator':
            value = withDelimiters(2, "'", '.');
            break;
          case 'amount_no_decimals_with_space_separator':
            value = withDelimiters(0, ' ', '.');
            break;
          case 'amount_with_space_separator':
            value = withDelimiters(2, ' ', ',');
            break;
          case 'amount_with_period_and_space_separator':
            value = withDelimiters(2, ' ', '.');
            break;
          default:
            value = withDelimiters(2, ',', '.');
        }

        return this.moneyFormat.replace(placeholder, value);
      }
    }
  );
}
